import { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from 'react-i18next';
import {
  LogOut, Home, FileText, Clock, Settings, DollarSign, Mic, History, Building2,
  Key, BarChart3, Cable, ShieldBan, Mail, Wrench, UserCheck, Contact, HardDrive, Target,
  Package, Receipt, Video, ChevronDown, PanelLeftClose, PanelLeft, Wallet
} from 'lucide-react';
import GlobalTimer from './GlobalTimer';
import NotificationBell from './NotificationBell';
import AttendanceButton from './AttendanceButton';
import GlobalSearch from './GlobalSearch';
import LanguageSwitcher from './LanguageSwitcher';
import UpdatePrompt from './UpdatePrompt';
import PushBanner from './PushBanner';
import MobileNav from './MobileNav';
import ToastContainer from './ToastContainer';
import BrandLogo from './BrandLogo';
import { useBrand } from '../providers/BrandProvider';

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  show: boolean;
}

function NavGroupSection({ group, collapsed, isActive }: {
  group: NavGroup;
  collapsed: boolean;
  isActive: (path: string) => boolean;
}) {
  const [open, setOpen] = useState(true);

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1 py-1">
        {group.items.filter(i => i.show).map(item => {
          const Icon = item.icon;
          const active = isActive(item.to);
          return (
            <Link key={item.to} to={item.to} aria-label={item.label}
              className={`relative flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 group ${
                active
                  ? 'bg-primary-700 text-white shadow-card'
                  : 'text-surface-600 hover:bg-surface-100 hover:text-surface-800'
              }`}>
              <Icon size={18} />
              <span className="absolute right-full mr-3 px-2.5 py-1 bg-surface-800 text-white text-xs
                             rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    );
  }

  const visibleItems = group.items.filter(i => i.show);
  if (visibleItems.length === 0) return null;

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-3 py-1.5 text-[11px] font-semibold
                   text-surface-400 uppercase tracking-widest hover:text-surface-600 transition-colors"
      >
        <span>{group.label}</span>
        <ChevronDown size={12} className={`transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="flex flex-col gap-0.5 mt-0.5">
          {visibleItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link key={item.to} to={item.to}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all duration-200 ${
                  active
                    ? 'bg-primary-50/60 text-primary-800 font-medium border-r-[3px] border-primary-700'
                    : 'text-surface-600 hover:bg-surface-100 hover:text-surface-800'
                }`}>
                <Icon size={18} className={active ? 'text-primary-600' : 'text-surface-400'} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, organization, clearAuth } = useAuthStore();
  const { t } = useTranslation();
  const brand = useBrand();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'customer_manager';
  const isAgent = user?.role === 'agent';
  const isCustomerViewer = user?.role === 'customer_viewer';
  const canSeeBilling = isAdmin || isManager;
  const canSeeVoice = isAdmin || isAgent;
  const canSeeAudit = isAdmin || isManager;
  const canSeeInternal = !isCustomerViewer;

  const orgId = organization?.id;

  const navGroups: NavGroup[] = [
    {
      label: t('nav.main', 'ראשי'),
      items: [
        { to: '/', label: t('nav.home'), icon: Home, show: true },
        { to: '/tickets', label: t('nav.tickets'), icon: FileText, show: true },
        ...(orgId ? [{ to: `/organizations/${orgId}`, label: t('nav.organization'), icon: Building2, show: true }] : []),
      ],
    },
    {
      label: t('nav.operations', 'תפעול'),
      items: [
        { to: '/work-logs', label: t('nav.workLogs'), icon: Clock, show: canSeeInternal },
        { to: '/voice', label: t('nav.voice'), icon: Mic, show: canSeeVoice },
        { to: '/attendance', label: t('nav.attendance'), icon: UserCheck, show: isAdmin || isAgent },
        { to: '/contacts', label: t('nav.contacts'), icon: Contact, show: isAdmin || isAgent },
        { to: '/meetings', label: t('nav.meetings'), icon: Video, show: isAdmin || isAgent },
      ],
    },
    {
      label: t('nav.sales', 'מכירות'),
      items: [
        { to: '/leads', label: t('nav.leads'), icon: Target, show: isAdmin || isAgent },
        { to: '/quotes', label: t('nav.quotes'), icon: Receipt, show: isAdmin || isAgent },
        { to: '/products', label: t('nav.products'), icon: Package, show: isAdmin },
      ],
    },
    {
      label: t('nav.analytics', 'ניתוח'),
      items: [
        { to: '/reports', label: t('nav.reports'), icon: BarChart3, show: canSeeInternal },
        { to: '/billing', label: t('nav.billing'), icon: DollarSign, show: canSeeBilling },
        { to: '/cashflow', label: t('nav.cashFlow'), icon: Wallet, show: isAdmin },
        { to: '/audit', label: t('nav.auditLog'), icon: History, show: canSeeAudit },
      ],
    },
    {
      label: t('nav.settings', 'הגדרות'),
      items: [
        { to: '/integrations', label: t('nav.integrations'), icon: Cable, show: isAdmin || isAgent },
        { to: '/admin', label: t('nav.admin'), icon: Settings, show: isAdmin || isAgent },
        { to: '/settings/system', label: t('nav.systemSettings'), icon: Wrench, show: isAdmin },
        { to: '/settings/backups', label: t('nav.backups'), icon: HardDrive, show: isAdmin },
        { to: '/api-platform', label: t('nav.api'), icon: Key, show: isAdmin },
        { to: '/blocklist', label: t('nav.blocklist'), icon: ShieldBan, show: isAdmin },
        { to: '/email-templates', label: t('nav.emailTemplates'), icon: Mail, show: isAdmin },
      ],
    },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const sidebarWidth = sidebarCollapsed ? 'w-[72px]' : 'w-[260px]';

  return (
    <div className="min-h-screen bg-surface-50 flex">
      {/* Sidebar — hidden on mobile */}
      <aside className={`hidden md:flex flex-col ${sidebarWidth} border-l border-surface-400/15 bg-surface-0
                        fixed top-0 right-0 h-screen z-30 transition-all duration-300`}>
        {/* Logo */}
        <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} h-16 px-4 border-b border-surface-400/10`}>
          {!sidebarCollapsed && (
            <Link to="/" className="flex items-center gap-2.5">
              <BrandLogo variant="sidebar" />
              <span className="text-lg font-bold text-surface-900 tracking-tight">{brand.productName}</span>
            </Link>
          )}
          {sidebarCollapsed && (
            <BrandLogo variant="sidebar" />
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 rounded-lg text-surface-400 hover:text-surface-600 hover:bg-surface-100 transition-colors"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        {/* Nav Groups */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-2" aria-label={t('aria.mainNav')}>
          {navGroups.map((group, i) => (
            <NavGroupSection key={i} group={group} collapsed={sidebarCollapsed} isActive={isActive} />
          ))}
        </nav>

        {/* User section at bottom */}
        {!sidebarCollapsed && (
          <div className="border-t border-surface-400/10 p-3">
            <Link to="/profile"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                isActive('/profile') ? 'bg-primary-50' : 'hover:bg-surface-50'
              }`}>
              <div className="avatar-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-surface-800 truncate">{user?.name}</p>
                <p className="text-xs text-surface-400 truncate">{organization?.name}</p>
              </div>
            </Link>
          </div>
        )}
      </aside>

      {/* Main content area */}
      <div className={`flex-1 flex flex-col min-h-screen ${sidebarCollapsed ? 'md:mr-[72px]' : 'md:mr-[260px]'} transition-all duration-300`}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-surface-0/80 backdrop-blur-xl border-b border-surface-400/10 shadow-card">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6 lg:px-8">
            {/* Mobile logo */}
            <div className="md:hidden">
              <Link to="/" className="flex items-center gap-2">
                <BrandLogo variant="mobile" />
                <span className="font-bold text-surface-900">{brand.productName}</span>
              </Link>
            </div>

            {/* Search */}
            <div className="hidden md:block flex-1 max-w-md">
              <GlobalSearch />
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-1.5">
              <div className="md:hidden">
                <GlobalSearch />
              </div>
              <AttendanceButton />
              <LanguageSwitcher />
              <NotificationBell />
              <div className="hidden md:block w-px h-6 bg-surface-200 mx-1" />
              <button
                onClick={handleLogout}
                aria-label={t('aria.logout')}
                className="p-2 rounded-xl text-surface-400 hover:text-danger-600 hover:bg-danger-50 transition-colors"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </header>

        {/* Impersonation banner */}
        {localStorage.getItem('tsi_impersonating') === 'true' && (
          <div className="bg-purple-600 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-3">
            <span>{t('admin.impersonatingAs')} <strong>{user?.name}</strong> ({user?.email})</span>
            <button
              onClick={() => {
                const originalToken = localStorage.getItem('tsi_original_token');
                if (originalToken) {
                  localStorage.setItem('token', originalToken);
                  localStorage.removeItem('tsi_original_token');
                }
                localStorage.removeItem('tsi_impersonating');
                window.location.href = '/';
              }}
              className="px-3 py-1 bg-white text-purple-700 rounded-md font-medium hover:bg-purple-50 transition-colors"
            >
              {t('admin.stopImpersonation')}
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-20 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Floating elements */}
      <GlobalTimer />
      <MobileNav />
      <PushBanner />
      <UpdatePrompt />
      <ToastContainer />
    </div>
  );
}
